import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad

Última actualización: Abril 2026

En Repostería Emprendedora, nos comprometemos a proteger tu privacidad. Esta política describe cómo recopilamos, usamos y protegemos tu información personal.

1. Información que Recopilamos
Recopilamos la información que nos proporcionas directamente al realizar una compra, incluyendo tu nombre, dirección de correo electrónico y datos de pago. Los datos de pago son procesados de forma segura por nuestro proveedor de pagos y no almacenamos información de tarjetas de crédito.

2. Uso de la Información
Utilizamos tu información para:
• Procesar y entregar tu compra (envío de los Ebooks por correo electrónico).
• Enviarte actualizaciones relacionadas con tu compra.
• Mejorar nuestros productos y servicios.

3. Protección de Datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, pérdida o alteración.

4. Compartir Información
No vendemos, intercambiamos ni transferimos tu información personal a terceros, excepto cuando sea necesario para procesar tu compra o cuando la ley lo requiera.

5. Cookies
Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies.

6. Tus Derechos
Tienes derecho a acceder, rectificar o eliminar tu información personal. Para ejercer estos derechos, contáctanos por correo electrónico.

7. Cambios en esta Política
Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página.
`.trim();
const termsContent = `
Términos y Condiciones

Última actualización: Abril 2026

Al acceder y realizar una compra en Repostería Emprendedora, aceptas los siguientes términos y condiciones.

1. Productos Digitales
Los productos ofrecidos son Ebooks en formato PDF. Una vez completada la compra, recibirás acceso inmediato a los archivos digitales a través del correo electrónico proporcionado.

2. Licencia de Uso
Al comprar nuestros Ebooks, obtienes una licencia personal e intransferible para uso individual. Queda prohibida la reproducción, distribución, reventa o compartición de los materiales con terceros.

3. Política de Reembolso
Todos los productos digitales son de acceso inmediato. Una vez realizada la compra, recibirás los materiales en tu correo electrónico.

4. Propiedad Intelectual
Todo el contenido de los Ebooks, incluyendo textos, imágenes, recetas y diseños, es propiedad exclusiva de Repostería Emprendedora y está protegido por las leyes de propiedad intelectual.

5. Resultados
Los resultados pueden variar según la dedicación, esfuerzo y circunstancias individuales de cada persona. No garantizamos ingresos específicos.

6. Limitación de Responsabilidad
Repostería Emprendedora no será responsable por daños directos, indirectos o consecuentes derivados del uso de nuestros productos digitales.

7. Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación.

8. Ley Aplicable
Estos términos se rigen por las leyes aplicables en la jurisdicción correspondiente.
`.trim();
function Modal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  if (!type) return null;
  const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-27">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3
    }} className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-28">
        <div className="flex items-center justify-between p-6 border-b border-chocolate/10" data-id="element-29">
          <h3 className="font-serif text-xl font-bold text-chocolate" data-id="element-30">
            {title}
          </h3>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-cream flex items-center justify-center hover:bg-cream-dark transition-colors" data-id="element-31">
            <X className="w-5 h-5 text-chocolate" data-id="element-32" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-33">
          <div className="text-chocolate-light leading-relaxed whitespace-pre-line text-sm md:text-base" data-id="element-34">
            {content}
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
interface FooterProps {
  onBuyClick?: () => void;
}
export function Footer({
  onBuyClick
}: FooterProps) {
  const [modal, setModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-chocolate text-cream-dark py-16 px-4 text-center border-t-4 border-gold" data-id="element-35">
        <div className="max-w-3xl mx-auto" data-id="element-36">
          <h2 className="font-serif text-2xl md:text-3xl font-bold mb-8 text-white" data-id="element-37">
            Tu momento de brillar en la cocina es ahora.
          </h2>
          <Button className="mx-auto mb-12" onClick={onBuyClick} data-id="element-38">
            ¡SÍ, QUIERO MI NEGOCIO DE REPOSTERÍA!
          </Button>

          <div className="flex items-center justify-center gap-4 mb-6 text-sm" data-id="element-39">
            <button onClick={() => setModal('privacy')} className="text-cream/70 hover:text-gold underline underline-offset-4 transition-colors" data-id="element-40">
              Política de Privacidad
            </button>
            <span className="text-cream/30" data-id="element-41">|</span>
            <button onClick={() => setModal('terms')} className="text-cream/70 hover:text-gold underline underline-offset-4 transition-colors" data-id="element-42">
              Términos y Condiciones
            </button>
          </div>

          <div className="text-sm opacity-60 space-y-2" data-id="element-43">
            <p data-id="element-44">
              © {new Date().getFullYear()} Repostería Emprendedora. Todos los
              derechos reservados.
            </p>
            <p data-id="element-45">
              Este sitio no es parte del sitio web de Facebook o Facebook Inc.
              Además, este sitio NO está respaldado por Facebook de ninguna
              manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
            </p>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-46">
        {modal && <Modal type={modal} onClose={() => setModal(null)} data-id="element-47" />}
      </AnimatePresence>
    </>;
}